import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { PermissionEnum } from '@api/enum';
import { ClientInstallationStatusEnum } from '@api/enum/client-installation.enum';
import useAuthPermission from '@hooks/use-auth-permission';
import { Button } from '@hyperflake/react-ui-library';
import { PageHeading, PageTemplate } from 'modules/shared/features/content';

import SelectDropdown from '@components/SelectDropDown/SelectDropDown';
import { ChevronDownIcon } from '@icon/index';

import ClientInstallationList from 'modules/client-installation/components/ClientInstallationList/ClientInstallationList';
import { useClientInstallationListData } from 'modules/client-installation/hooks/useClientInstallationListData';

type FilterValue = 'ALL' | ClientInstallationStatusEnum;

const ClientInstallationPage = () => {
    const { hasPermission } = useAuthPermission();
    const navigate = useNavigate();

    const [filterValue, setFilterValue] = useState<FilterValue>('ALL');

    const filterOptions = useMemo(
        () => [
            { label: 'All', value: 'ALL' as const },
            { label: 'Complete', value: ClientInstallationStatusEnum.COMPLETE },
            { label: 'On Hold', value: ClientInstallationStatusEnum.ON_HOLD },
            { label: 'Pending', value: ClientInstallationStatusEnum.PENDING },
        ],
        []
    );

    const selectedLabel = filterOptions.find((o) => o.value === filterValue)?.label ?? 'All';

    const { clientInstallationListData, isLoading } = useClientInstallationListData({ filterValue });

    return (
        <PageTemplate>
            <div className="flex justify-between items-center">
                <PageHeading>Client Installations</PageHeading>

                <div className="flex items-center gap-3">
                    <div>
                        <SelectDropdown
                            className="btn-dropdown btn-secondary btn-secondary-hover"
                            value={filterValue}
                            onChange={(v) => setFilterValue(v as FilterValue)}
                            options={filterOptions}
                            render={() => (
                                <span className="flex items-center gap-2 text-grayscale-500 font-medium">
                                    View:
                                    <span className="text-grayscale-900 font-semibold">{selectedLabel}</span>
                                    <span>
                                        <ChevronDownIcon className="text-grayscale-400" strokeWidth="2" />
                                    </span>
                                </span>
                            )}
                        />
                    </div>

                    {hasPermission(PermissionEnum.CLIENT_INSTALLATION_CREATE) && (
                        <Link to={`/client-installations/add`}>
                            <Button onClick={() => navigate('/client-installations/add')}>Add Client</Button>
                        </Link>
                    )}
                </div>
            </div>

            <ClientInstallationList data={clientInstallationListData} isLoading={isLoading} />
        </PageTemplate>
    );
};

export default ClientInstallationPage;
